import { Component, type ErrorInfo, type JSX, type ReactNode } from 'react';
import { getBrowserInfo, getSystemOsInfo } from '../services/errorHandling/browserInfo.util';
import type { ErrorLogPayload, ErrorSeverity } from '../services/errorHandling/errorHandling.types';
import { findRoutePageComponentName, parseTopStackFrame } from '../services/errorHandling/stackFrame.util';
import {
  buildBaseErrorLogPayload,
  getAttachedErrorLogPayload,
  getOrCreateErrorLogPayload,
  logError,
} from '../services/errorLogger';
import { isChunkLoadError, type RetriedChunkLoadError } from '../util/lazyWithRetry';
import { isAccessDeniedRedirectError } from './accessErrorNavigation';
import { isOnErrorPage, redirectToErrorPage } from './errorPageNavigation.util';
import './errorPage.css';

export type ErrorBoundaryMode = 'isolate' | 'redirect';

// react-router renders every matched route's element inside RenderedRoute, so the
// component just inside the innermost RenderedRoute frame is that route's page.
const ROUTER_FRAME_NAMES: ReadonlySet<string> = new Set(['RenderedRoute']);

// Bounds how long the redirect below waits for the error log to finish sending —
// long enough for a normal request, short enough that a stuck/unreachable logging
// endpoint can never leave the crashed page stuck on-screen indefinitely.
const LOG_BEFORE_REDIRECT_TIMEOUT_MS = 3000;

function settleWithinTimeout(promise: Promise<unknown>, timeoutMs: number): Promise<void> {
  return Promise.race([
    promise.then(
      () => {},
      () => {}
    ),
    new Promise<void>((resolve) => {
      setTimeout(resolve, timeoutMs);
    }),
  ]);
}

interface ErrorBoundaryProps {
  /** Identifies which section/component crashed — the single most useful field for tracing. */
  readonly name: string;
  /** Set only on the app-wide boundary (GlobalErrorBoundary). Its `name` ("app") says
   * nothing about what crashed, so the logged componentName is worked out from the
   * page instead — see componentDidCatch. */
  readonly isAppRoot?: boolean;
  readonly children: ReactNode;
  /** 'isolate' (default) renders `fallback` in place; 'redirect' sends the user to the generic error page. */
  readonly mode?: ErrorBoundaryMode;
  readonly fallback?: (error: Error, reset: () => void) => ReactNode;
  /** When any value in this array changes, the boundary resets automatically (e.g. route params). */
  readonly resetKeys?: ReadonlyArray<unknown>;
  /** Notified after this boundary has already logged the error — e.g. so the app-wide
   * boundary can also mirror it into ErrorContext for the redirect page's dev display. */
  readonly onError?: (error: Error, payload: ErrorLogPayload) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

function DefaultFallback({
  name,
  onRetry,
}: {
  readonly name: string;
  readonly onRetry: () => void;
}): JSX.Element {
  return (
    <div
      className="d-flex flex-column align-items-center gap-3 p-4 text-center"
      style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}
    >
      <h3 className="h6 mb-0" style={{ color: '#0f172a' }}>
        Something went wrong
      </h3>
      <p className="small mb-0" style={{ color: '#475569' }}>
        The {name} section could not be displayed.
      </p>
      <button type="button" className="ao-fallback-btn btn-sm" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { error: null };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (isAccessDeniedRedirectError(error)) {
      // Intentional navigation-via-throw (see accessErrorNavigation.ts) — not a real crash.
      return;
    }

    const componentStack = errorInfo.componentStack ?? undefined;
    const frame = parseTopStackFrame(componentStack);
    const { browserName, browserVersion, screenResolution } = getBrowserInfo();
    const { systemOS, systemOSVersion } = getSystemOsInfo();

    // componentName = the boundary that caught the error. A section boundary
    // (<ErrorBoundary name="Report view">) is already specific, so its own `name` is
    // used as-is. The app-wide boundary (GlobalErrorBoundary, `isAppRoot`) is only
    // ever called "app", so for it the name comes from, in order:
    //  1. `lazyChunkName` — the lazyWithRetry key, set when a page's chunk failed to
    //     load (there's no component stack for the page in that case);
    //  2. the page component react-router rendered, read from the componentStack
    //     (e.g. "ReportChangeOnly") — needs the production build to keep function
    //     names (set in the minifier options of the webpack config override);
    //  3. the current pathname, which always exists.
    // `boundaryName` and `pageComponentName` are logged separately as well, so a
    // section crash still says which page it was on.
    const lazyChunkName = (error as RetriedChunkLoadError).lazyChunkName;
    const pageComponentName = findRoutePageComponentName(componentStack, ROUTER_FRAME_NAMES);
    const currentPathname = typeof window === 'undefined' ? undefined : window.location.pathname || undefined;
    const effectiveComponentName =
      this.props.isAppRoot === true
        ? lazyChunkName ?? pageComponentName ?? currentPathname ?? this.props.name
        : this.props.name;

    // The same component crashing again on the same page (e.g. on a refresh, when
    // the underlying data/API keeps failing the same way) reuses the same
    // correlationId instead of minting a new one every time.
    const dedupKey = `component:${effectiveComponentName}:${currentPathname ?? ''}`;
    // Set by lazyWithRetry when this chunk-load failure already went through its one
    // automatic reload-and-retry (on the previous page load) and is failing again —
    // surfaced separately from `message` (which can be shown to the user on the
    // global error page) so the log itself distinguishes a persistent load failure
    // from a first-attempt one without anyone having to guess from volume alone.
    const chunkRetryExhausted = (error as RetriedChunkLoadError).chunkRetryExhausted === true;
    const isChunkFailure = isChunkLoadError(error);

    // silent-sso-callback runs in a hidden iframe (Keycloak's silent SSO check) with
    // no user-visible UI of its own — a chunk-load timeout there never reaches a real
    // person and isn't actionable the same way a foreground page's chunk failure is.
    // Production logs showed this single case alone accounting for a large share of
    // all chunk-load noise; demoting its severity (rather than dropping the log
    // entirely) keeps it visible for volume/trend purposes without it drowning out
    // genuinely user-facing failures at the same 'critical' level.
    const isBenignBackgroundChunkFailure = isChunkFailure && lazyChunkName === 'silent-sso-callback';
    // standard-error / public-standard-error is the page THIS APP redirects users to
    // in order to show them an error — if its own lazy chunk also fails to load, the
    // user is left looking at a blank/broken screen with no message at all, which is
    // strictly worse than the failure that sent them there in the first place. Tagged
    // distinctly so this compounding case is never mistaken for an ordinary one-off
    // chunk failure when triaging.
    // Prefer the lazyChunkName tag when it's present (the normal case), but fall back to
    // isOnErrorPage() directly — the tag is only ever set inside lazyWithRetry's own
    // try/catch around `importer()` (see its comment), so a chunk failure that somehow
    // reaches here without going through that path would otherwise be missed entirely.
    const isErrorPageOwnChunkFailure =
      isChunkFailure &&
      (lazyChunkName === 'standard-error' || lazyChunkName === 'public-standard-error' || isOnErrorPage());

    const severity: ErrorSeverity = isBenignBackgroundChunkFailure ? 'info' : 'critical';
    const retryContextParts: Array<string> = [];
    if (chunkRetryExhausted) {
      retryContextParts.push('chunk-load failed again after lazyWithRetry already reloaded the page once');
    }
    if (isBenignBackgroundChunkFailure) {
      retryContextParts.push(
        'silent-sso-callback runs in a hidden iframe with no user-visible UI — this timeout has no user impact'
      );
    }
    if (isErrorPageOwnChunkFailure) {
      retryContextParts.push(
        "this is the global error page's own lazy chunk failing to load — the user sees a blank/broken screen, not an error message"
      );
    }

    // An error that was already logged upstream (e.g. an API failure the axios
    // interceptor logged with its X-Request-ID, then rethrown into render) keeps that
    // payload and correlationId — same root cause, same Ref. — and isn't logged again.
    const upstreamPayload = getAttachedErrorLogPayload(error);
    const payload: ErrorLogPayload = upstreamPayload ?? getOrCreateErrorLogPayload(dedupKey, () => ({
      ...buildBaseErrorLogPayload('component-boundary', severity, error.message, error.stack),
      componentName: effectiveComponentName,
      childComponentName: frame?.functionName,
      boundaryName: this.props.name,
      ...(pageComponentName === undefined ? {} : { pageComponentName }),
      componentStack,
      originFile: frame?.fileName,
      originLine: frame?.line,
      originColumn: frame?.column,
      originFunctionName: frame?.functionName,
      browserName,
      browserVersion,
      screenResolution,
      systemOS,
      systemOSVersion,
      ...(retryContextParts.length > 0 ? { retryContext: retryContextParts.join('; ') } : {}),
    }));

    if (this.props.mode === 'redirect' && !isOnErrorPage()) {
      // This boundary's own render() returns null once it has caught an error (see
      // below), which unmounts the router along with everything else beneath it — a
      // soft route change has nothing left to render into at that point, so this must
      // use a hard navigation. Waiting for the log first (bounded by a timeout, so an
      // unreachable logging endpoint can never strand the crashed page) stops that
      // reload from cancelling the in-flight POST before the server sees it.
      const logged = upstreamPayload
        ? Promise.resolve()
        : settleWithinTimeout(logError(payload), LOG_BEFORE_REDIRECT_TIMEOUT_MS);
      void logged.then(() => {
        this.props.onError?.(error, payload);
        redirectToErrorPage(payload.correlationId, payload.message, { preferHardNavigation: true });
      });
    } else {
      // Fire-and-forget: isolate mode never navigates anywhere, so there's nothing
      // that could cancel this in-flight POST.
      if (!upstreamPayload) {
        void logError(payload);
      }
      this.props.onError?.(error, payload);
    }

    this.setState({ error });
  }

  public componentDidUpdate(previousProps: ErrorBoundaryProps): void {
    if (!this.state.error || !this.props.resetKeys || !previousProps.resetKeys) {
      return;
    }

    const hasChanged = this.props.resetKeys.some(
      (key, index) => key !== previousProps.resetKeys?.[index]
    );

    if (hasChanged) {
      this.reset();
    }
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  public render(): ReactNode {
    const { error } = this.state;
    const { mode = 'isolate', fallback, name, children } = this.props;

    if (!error) {
      return children;
    }

    if (mode === 'redirect') {
      if (isOnErrorPage()) {
        // We're already on the global error page and IT just crashed — componentDidCatch's
        // own redirect (above) deliberately never fires again here (isOnErrorPage() guards
        // it, to avoid a redirect loop), so returning null unconditionally (as this branch
        // used to) leaves the user staring at a permanently blank screen with no way
        // forward — strictly worse than whatever originally sent them to this page. Render
        // a minimal, dependency-free fallback instead: no CSS classes or other app
        // components, so it can't itself throw the same way whatever crashed here did.
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              padding: 24,
              textAlign: 'center',
              fontFamily: 'sans-serif',
            }}
          >
            <h1 style={{ fontSize: 20, marginBottom: 12, color: '#0f172a' }}>Something went wrong</h1>
            <p style={{ marginBottom: 20, color: '#475569' }}>
              We were unable to display the error page itself. Please refresh to try again.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 20px',
                borderRadius: 4,
                border: '1px solid #cbd5e1',
                backgroundColor: '#0f172a',
                color: '#fff',
                cursor: 'pointer',
              }}
            >
              Refresh page
            </button>
          </div>
        );
      }
      // The full-page redirect is already in flight from componentDidCatch.
      return null;
    }

    if (fallback) {
      return fallback(error, this.reset);
    }

    return <DefaultFallback name={name} onRetry={this.reset} />;
  }
}
